/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from 'react';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Typography,
    Box,
    IconButton,
    InputAdornment,
    makeStyles,
} from '@material-ui/core';
import {
    Visibility as ViewIcon,
    GetApp as DownloadIcon,
    CreditCard as PaymentIcon,
    FileCopy as CopyIcon,
} from '@material-ui/icons';
import { useNotify, useRefresh } from 'react-admin';
import { useDataProvider } from 'react-admin';

const useStyles = makeStyles(theme => ({
    buttonContainer: {
        display: 'flex',
        gap: theme.spacing(1),
        alignItems: 'center',
    },
    actionButton: {
        minWidth: 32,
        width: 32,
        height: 32,
        borderRadius: 8,
        border: 'none',
        transition: 'all 0.2s ease',
        '&:hover': {
            transform: 'translateY(-1px)',
            // boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        },
    },
    viewButton: {
        backgroundColor: '#f8fafc',
        color: '#64748b',
        '&:hover': {
            backgroundColor: '#e2e8f0',
            color: '#475569',
        },
    },
    downloadButton: {
        backgroundColor: '#dbeafe',
        color: '#3b82f6',
        '&:hover': {
            backgroundColor: '#bfdbfe',
            color: '#2563eb',
        },
    },
    payButton: {
        backgroundColor: '#fef3c7',
        color: '#d97706',
        width: 'fit-content',
        padding: theme.spacing(0, 1),
        display: 'flex',
        gap: theme.spacing(2),
        '&:hover': {
            backgroundColor: '#fde68a',
            color: '#b45309',
        },
        '& .MuiIconButton-label': {
            display: 'flex',
            gap: theme.spacing(0.5),
        },
    },
    payButtonText: {
        fontWeight: 500,
        fontSize: '0.875rem',
    },
    dialogContent: {
        minWidth: 400,
        padding: theme.spacing(3),
    },
    formField: {
        marginBottom: theme.spacing(2.5),
        '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '& fieldset': {
                borderColor: '#e2e8f0',
            },
            '&:hover fieldset': {
                borderColor: '#cbd5e1',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#3b82f6',
                borderWidth: 2,
            },
        },
        '& .MuiInputLabel-root': {
            color: '#64748b',
            '&.Mui-focused': {
                color: '#3b82f6',
            },
        },
    },
    amountText: {
        fontWeight: 600,
        fontSize: '1.25rem',
        color: '#1e293b',
        marginBottom: theme.spacing(3),
        padding: theme.spacing(2),
        backgroundColor: '#f8fafc',
        borderRadius: 8,
        border: '1px solid #e2e8f0',
    },
    copyIcon: {
        cursor: 'pointer',
        color: '#64748b',
        '&:hover': {
            color: '#3b82f6',
        },
    },
    dialogTitle: {
        '& .MuiTypography-root': {
            fontSize: '1.25rem',
            fontWeight: 600,
            color: '#1e293b',
        },
    },
    dialogActions: {
        padding: theme.spacing(2, 3, 3),
        gap: theme.spacing(1),
    },
    cancelButton: {
        color: '#64748b',
        borderColor: '#e2e8f0',
        borderRadius: 8,
        textTransform: 'none',
        fontWeight: 500,
        '&:hover': {
            backgroundColor: '#f8fafc',
            borderColor: '#cbd5e1',
        },
    },
    confirmButton: {
        backgroundColor: '#3b82f6',
        borderRadius: 8,
        textTransform: 'none',
        color: 'white',
        fontWeight: 500,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        '&:hover': {
            backgroundColor: '#2563eb',
            boxShadow: '0 4px 12px rgba(59,130,246,0.4)',
        },
        '&:disabled': {
            backgroundColor: '#94a3b8',
        },
    },
}));

interface ActionFieldProps {
    record?: any;
    source?: string;
    label?: string;
}

const ActionField: React.FC<ActionFieldProps> = ({ record }) => {
    const classes = useStyles();
    const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
    const [iban, setIban] = useState(''); // Default IBAN
    const [transactionNumber, setTransactionNumber] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();

    useEffect(() => {
        setIban(record.payment_info?.iban || record.livreur?.rib || '');
        setTransactionNumber(record.payment_info?.transaction_number || '');
    }, [record]);

    const API_URL = process.env.REACT_APP_REST_API_URL;

    const handleView = fileId => {
        if (fileId) {
            const viewLink = `${API_URL}/files/${fileId}/view`;
            window.open(viewLink, '_blank');
        }
    };

    const handleDownload = fileId => {
        if (fileId) {
            const downloadLink = `${API_URL}/files/${fileId}/download`;
            window.open(downloadLink, '_blank');
        }
    };

    const handlePayClick = () => {
        setPaymentDialogOpen(true);
    };

    const handlePaymentDialogClose = () => {
        setPaymentDialogOpen(false);
        setTransactionNumber('');
    };

    const handleCopyIban = () => {
        navigator.clipboard
            .writeText(iban)
            .then(() => {
                notify('IBAN copié dans le presse-papiers', {
                    type: 'success',
                });
            })
            .catch(() => {
                notify('Erreur lors de la copie', { type: 'error' });
            });
    };

    const handlePaymentConfirm = async () => {
        // if (!transactionNumber.trim()) {
        //     notify('Veuillez saisir le numéro de transaction', {
        //         type: 'error',
        //     });
        //     return;
        // }

        try {
            setIsLoading(true);
            await dataProvider.update('factures-boxroad', {
                id: record.id + '/pay',
                data: {
                    payment_info: {
                        transaction_number: transactionNumber,
                        iban: iban,
                    },
                },
            });
            notify('Paiement confirmé avec succès', { type: 'success' });
            refresh();
            handlePaymentDialogClose();
        } catch (error) {
            notify('Erreur lors de la confirmation du paiement', {
                type: 'error',
            });
        } finally {
            setIsLoading(false);
        }
    };

    if (!record) {
        return null;
    }

    return (
        <>
            <Box className={classes.buttonContainer}>
                <IconButton
                    className={`${classes.actionButton} ${classes.downloadButton}`}
                    onClick={e => {
                        e.stopPropagation();
                        handleDownload(record.file);
                    }}
                    title="Télécharger"
                >
                    <DownloadIcon fontSize="small" />
                </IconButton>

                {record.status === 'not_paid' && (
                    <IconButton
                        className={`${classes.actionButton} ${classes.payButton}`}
                        onClick={e => {
                            e.stopPropagation();
                            handlePayClick();
                        }}
                        title="Effectuer le paiement"
                    >
                        <PaymentIcon fontSize="small" />
                        <span className={classes.payButtonText}>Payer</span>
                    </IconButton>
                )}
            </Box>

            <Dialog
                open={paymentDialogOpen}
                onClose={handlePaymentDialogClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    style: {
                        borderRadius: 12,
                        boxShadow:
                            '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    },
                }}
            >
                <DialogTitle className={classes.dialogTitle}>
                    Confirmation de paiement
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    <Typography className={classes.amountText}>
                        Montant TTC à payer: {record.price || '0.00'} €
                    </Typography>

                    <TextField
                        fullWidth
                        label="IBAN"
                        value={iban}
                        onChange={e => setIban(e.target.value)}
                        className={classes.formField}
                        variant="outlined"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={handleCopyIban}
                                        className={classes.copyIcon}
                                        size="small"
                                        title="Copier l'IBAN"
                                    >
                                        <CopyIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    <TextField
                        fullWidth
                        label="Numéro de transaction"
                        value={transactionNumber}
                        onChange={e => setTransactionNumber(e.target.value)}
                        className={classes.formField}
                        variant="outlined"
                        // required
                        placeholder="Saisissez le numéro de transaction"
                    />
                </DialogContent>

                <DialogActions className={classes.dialogActions}>
                    <Button
                        onClick={handlePaymentDialogClose}
                        variant="outlined"
                        className={classes.cancelButton}
                    >
                        Annuler
                    </Button>
                    <Button
                        onClick={handlePaymentConfirm}
                        variant="contained"
                        className={classes.confirmButton}
                        disabled={isLoading}
                    >
                        {isLoading
                            ? 'Confirmation...'
                            : 'Confirmer le paiement'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ActionField;
